import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Container, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Tippy from '@tippy.js/react';
import {
    TimeSelectorRow,
    SearchBox,
    GeneralSelect,
    Switch,
    LoadingStatusWrapper
} from 'sharedComponents';
import { IconInfo } from 'svgIcons/MotionPortalIcons';
import { useNewCapability } from 'sharedHooks';
import AssetMultiSelect from './AssetMultiSelect';
import EventTypeFilter from './EventTypeFilter';
import {
    getDateKindOptions,
    getCauseOfDriveEventsOptions,
    getDefaultTimeZoneValue,
    getDefaultCauseOfDriveEvent,
    getFormattedAssets,
    getSelectedEventData,
    isCrossOrgNotification,
    isEventGroupNotification
} from './helpers';
import { getFormattedAsset } from '../helpers';
import { EventTypeAssetApplicability, causeOfEvents, dateKind as initialDateKind } from '../constants';
import { selectState } from 'helpers/filterHelper';
import { apiCallStatus } from 'helpers/constants';

const EventLogFilterCMDComponent = ({ ...props }) => {
    const { t: translate } = useTranslation();

    const capability = useNewCapability();

    const [selectedAssetList, setSelectedAssetList] = useState([]);

    const dateKindOptions = getDateKindOptions(translate);
    const causeOfEventOptions = getCauseOfDriveEventsOptions(translate);
    const [isVisible, setIsVisible] = useState(false);

    const {
        selectedAssets,
        filters,
        assetCapabilityLoadingStatus,
        assetListCapabilities,
        capabilitiesLoadingStatus,
        dateKind,
        assets,
        selectedAsset,
        eventLogLoadingStatus,
        selectedOrganization,
        maintainSelectedEventData,
        selectedNotification,
        isAdmin
    } = props.model;

    const {
        setFilters,
        setDateKind,
        maintainEventData,
        setSelectedNotification
    } = props.actions;
    const selectedEventTypes = getSelectedEventData(maintainSelectedEventData, selectedAsset, selectedOrganization, filters.eventTypes, { name: 'eventTypes' });
    const defaultTimeZoneValue = useMemo(() => getDefaultTimeZoneValue(initialDateKind.Local, translate), [translate]);
    const selectedDateKind = getSelectedEventData(maintainSelectedEventData, selectedAsset, selectedOrganization, defaultTimeZoneValue, { name: 'timeZone' });

    useEffect(() => {
        selectedAssets?.length &&
            setSelectedAssetList(selectedAssets);
    }, [selectedAssets]);

    const handleSelectDate = (from, to) => {
        if (from && to) {
            const filter = {
                ...filters,
                dateSelection: { from, to }
            };
            setFilters(filter);
        }
    };

    useEffect(() => {
        const handleScroll = () => setIsVisible(false);
        const removeScrollListener = () => window.removeEventListener('scroll', handleScroll, true);
        window.addEventListener('scroll', handleScroll, true);
        return removeScrollListener;
    }, []);

    // SET EVENT SEARCH FROM NOTIFICATION
    useEffect(() => {
        if (selectedNotification && isEventGroupNotification(selectedNotification) && !isCrossOrgNotification(selectedNotification, selectedOrganization)) {
            const searchTerm = selectedNotification?.properties?.eventMessage?.substring(0, 30);
            setFilters({ eventName: searchTerm });
            setSelectedNotification(null);
        }
    }, [selectedNotification, selectedOrganization]);

    useEffect(() => {
        if (selectedEventTypes) {
            setFilters({ eventTypes: selectedEventTypes });
        }
    }, [selectedEventTypes]);

    useEffect(() => {
        if (selectedDateKind) {
            setDateKind(selectedDateKind?.id);
        }
    }, [selectedDateKind]);

    const handleFilterByEventType = (typeID) => {
        let eventTypes = [];
        eventTypes = selectState(filters.eventTypes, typeID);
        setFilters({ eventTypes });
        props.setIsExapnded(false);
        maintainEventData(eventTypes, selectedAsset, selectedOrganization, { name: 'eventTypes' });
    };

    const onMenuClose = () => {
        props.setSelectedAssets(selectedAssetList);
    };

    return (
        <Container className='event-log-filter-content'>
            <LoadingStatusWrapper loadingStatus={[
                assetCapabilityLoadingStatus
            ]}>
                <div className='filter-area'>
                    <Row>
                        <Col md='auto'>
                            <label htmlFor='event-timezone-selector' >{translate('ABB.Powertrain.Frontend.occurredOnLbl')}</label>
                            <Row className='date-type-and-period-selector g-0'>
                                <GeneralSelect
                                    className='timezone-ddn'
                                    inputId='event-timezone-selector'
                                    options={dateKindOptions}
                                    getOptionLabel={option => option.title}
                                    getOptionValue={option => option.id}
                                    defaultValue={getDefaultTimeZoneValue(dateKind, translate)}
                                    menuShouldScrollIntoView={false}
                                    onChange={dateKind => {
                                        setDateKind(dateKind.id);
                                        maintainEventData(dateKind, selectedAsset, selectedOrganization, { name: 'timeZone' });
                                    }}
                                    isSearchable={false}
                                    isDisabled={eventLogLoadingStatus === apiCallStatus.LOADING}
                                />
                                <Col md='auto' className='time-select-row-wrapper'>
                                    <TimeSelectorRow
                                        fromDate={filters?.dateSelection?.from}
                                        toDate={filters?.dateSelection?.to}
                                        label=''
                                        handleSelectDate={handleSelectDate}
                                        maxDaysRange={90}
                                        maxDaysBack={isAdmin ? 365 : 90}
                                        preSelectedDays={7}
                                        numberOfMonths={2}
                                        showLoading={false}
                                        footerIntervalText={translate('ABB.Powertrain.Frontend.datePickerMax90DaysNote')}
                                        disabled={eventLogLoadingStatus === apiCallStatus.LOADING}
                                    />
                                </Col>
                            </Row>
                        </Col>
                        <Col className='event-log-filter' md='auto'>
                            <EventTypeFilter {...props}
                                assetType={EventTypeAssetApplicability.DRIVE}
                                selectedStates={filters.eventTypes}
                                selectState={handleFilterByEventType}
                                disabled={eventLogLoadingStatus === apiCallStatus.LOADING}
                            />
                        </Col>
                        <Row className='event-filter-dropdowns'>
                            <Col md='auto'>
                                <GeneralSelect
                                    className='cause-of-event-ddn'
                                    options={causeOfEventOptions}
                                    defaultValue={getDefaultCauseOfDriveEvent(filters.causeOfEvent || causeOfEvents.All, translate)}
                                    getOptionLabel={option => option.title}
                                    getOptionValue={option => option.id}
                                    onChange={(causeOfEvent) => setFilters({ causeOfEvent: causeOfEvent.id })}
                                    menuShouldScrollIntoView={false}
                                    isSearchable={false}
                                    isDisabled={eventLogLoadingStatus === apiCallStatus.LOADING}
                                />
                            </Col>
                            <Col md='auto'>
                                <SearchBox
                                    onChange={(searchTerm) => setFilters({ eventName: searchTerm })}
                                    value={filters.eventName}
                                    placeholder={translate('ABB.Powertrain.Frontend.searchEventByNameLbl')}
                                    disabled={eventLogLoadingStatus === apiCallStatus.LOADING}
                                />
                            </Col>
                        </Row>
                        <Row className='event-filter-switches'>
                            <Col md='auto' className='event-list-switch'>
                                <Switch
                                    onChange={() => setFilters({ groupByNames: !filters.groupByNames })}
                                    checked={filters.groupByNames}
                                    disabled={eventLogLoadingStatus === apiCallStatus.LOADING}
                                />
                                <div className='event-list-control-label'>
                                    {translate('ABB.Powertrain.Frontend.eventListFilterGroupByNamesLbl')}
                                </div>
                            </Col>
                            <Col md='auto' className='event-list-switch'>
                                <Switch
                                    onChange={() => setFilters({ withCommentsOnly: !filters.withCommentsOnly })}
                                    checked={filters.withCommentsOnly}
                                    disabled={eventLogLoadingStatus === apiCallStatus.LOADING}
                                />
                                <div className='event-list-control-label'>
                                    {translate('ABB.Powertrain.Frontend.eventListFilterWithCommentsOnlyLbl')}
                                </div>
                            </Col>
                            <Col md='auto' className='event-list-switch'>
                                <Switch
                                    onChange={() => setFilters({ withDataLoggersOnly: !filters.withDataLoggersOnly, withBinaryLoggerOnly: !filters.withDataLoggersOnly })}
                                    checked={filters.withDataLoggersOnly}
                                    disabled={eventLogLoadingStatus === apiCallStatus.LOADING}
                                />
                                <div className='event-list-control-label'>
                                    {translate('ABB.Powertrain.Frontend.eventListFilterWithDataLoggersLbl')}
                                </div>
                            </Col>
                        </Row>
                        <Row className='event-filter-drive-selector'>
                            <div className='drive-selector-label'>
                                <label htmlFor='multi-asset-selector' className='mb-0'>
                                    {translate('ABB.Powertrain.Frontend.eventLogFilterSelectAssetsLbl')}
                                </label>
                                <Tippy
                                    content={translate('ABB.Powertrain.Frontend.eventListAssetsInfoText')}
                                    interactive={true}
                                    animation='scale'
                                    theme='light-border'
                                    trigger='manual'
                                    visible={isVisible}
                                    onClickOutside={() => setIsVisible(false)}
                                    placement='auto'
                                >
                                    <button
                                        type='button'
                                        className='info-icon-wrapper'
                                        aria-label={translate('ABB.Powertrain.Frontend.eventListAssetsInfoText')}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            setIsVisible(v => !v);
                                        }}
                                    >
                                        <IconInfo color={'black'} />
                                    </button>
                                </Tippy>
                            </div>
                            <AssetMultiSelect
                                inputId='multi-asset-selector'
                                onChange={(assets) => {
                                    setIsVisible(false);
                                    setSelectedAssetList(assets);
                                }}
                                onMenuOpen={() => setIsVisible(false)}
                                onMenuClose={() => {
                                    setIsVisible(false);
                                    onMenuClose();
                                }}
                                isLoading={capabilitiesLoadingStatus === apiCallStatus.LOADING}
                                isDisabled={!capability.checkEventListMultipleAssetsCapability() ||
                                    capabilitiesLoadingStatus === apiCallStatus.LOADING ||
                                    eventLogLoadingStatus === apiCallStatus.LOADING}
                                options={getFormattedAssets(
                                    assetListCapabilities,
                                    assets, selectedAsset
                                )}
                                selectedOptions={selectedAssetList}
                                lockedOption={getFormattedAsset(selectedAsset)}
                                isClearable={false}
                                allowSelectAll={true}
                            />
                        </Row>
                    </Row>
                </div>
            </LoadingStatusWrapper>
        </Container>
    );
};

EventLogFilterCMDComponent.propTypes = {
    actions: PropTypes.shape({
        setFilters: PropTypes.func, // Function to set filters
        setDateKind: PropTypes.func, // Function to set the date kind
        maintainEventData: PropTypes.func, // Function to maintain event data
        setSelectedNotification: PropTypes.func, // Function to set the selected notification
    }), // Actions object containing functions
    model: PropTypes.shape({
        selectedAssets: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string, // Asset ID
                name: PropTypes.string, // Asset name
            })
        ), // Array of selected assets
        selectedOrganization: PropTypes.shape({
            id: PropTypes.string, // Organization ID
            name: PropTypes.string, // Organization name
        }), // Selected organization
        filters: PropTypes.shape({
            eventTypes: PropTypes.arrayOf(PropTypes.string), // Array of selected event types
            causeOfEvent: PropTypes.string, // Selected cause of event
            eventName: PropTypes.string, // Search term for event name
            groupByNames: PropTypes.bool, // Whether to group by names
            withCommentsOnly: PropTypes.bool, // Whether to filter by events with comments only
            withDataLoggersOnly: PropTypes.bool, // Whether to filter by events with data loggers only
            dateSelection: PropTypes.shape({
                from: PropTypes.string, // Start date of the selection
                to: PropTypes.string, // End date of the selection
            }), // Date selection range
        }), // Filters applied to the event log
        assetCapabilityLoadingStatus: PropTypes.string, // Loading status for asset capabilities
        assetListCapabilities: PropTypes.oneOfType([
            PropTypes.arrayOf(
                PropTypes.shape({
                    id: PropTypes.string, // Capability ID
                    name: PropTypes.string, // Capability name
                })
            ),
            PropTypes.object,
        ]), // Asset list capabilities
        capabilitiesLoadingStatus: PropTypes.string, // Loading status for capabilities
        dateKind: PropTypes.number, // Selected date kind (e.g., UTC or local)
        assets: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string, // Asset ID
                name: PropTypes.string, // Asset name
            })
        ), // Array of available assets
        selectedAsset: PropTypes.shape({
            id: PropTypes.string, // Selected asset ID
            name: PropTypes.string, // Selected asset name
        }), // Currently selected asset
        eventLogLoadingStatus: PropTypes.string, // Loading status for the event log
        maintainSelectedEventData: PropTypes.bool, // Added validation for maintainSelectedEventData
        selectedNotification: PropTypes.shape({ // Added validation for selectedNotification
            organizationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Organization id of the notification
            properties: PropTypes.shape({
                eventMessage: PropTypes.string, // Event message from the notification
            }), // Properties of the selected notification
        }),
        isAdmin: PropTypes.bool, // Boolean indicating if the user is an admin
    }), // Model containing state data
    setSelectedAssets: PropTypes.func, // Function to set selected assets
    setIsExapnded: PropTypes.func, // Function to set the expanded state
};

export default EventLogFilterCMDComponent;
